"use client";

import { useEffect } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { useReducedMotion } from "framer-motion";

export function useMotionControl() {
  const prefersReduced = useReducedMotion();
  const isMobile = useMediaQuery("(max-width: 768px)");
  return { motionAllowed: !prefersReduced && !isMobile };
}

export function useParallaxTilt(
  ref: React.RefObject<HTMLElement>,
  intensity = 10
) {
  const { motionAllowed } = useMotionControl();
  useEffect(() => {
    if (!motionAllowed) return;
    const el = ref.current;
    if (!el) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const offsetX = ((x / rect.width) - 0.5) * intensity;
      const offsetY = ((y / rect.height) - 0.5) * intensity;
      el.style.transform = `rotateX(${-offsetY}deg) rotateY(${offsetX}deg)`;
    };

    const reset = () => {
      el.style.transform = "rotateX(0deg) rotateY(0deg)";
    };

    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseleave", reset);
    return () => {
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("mouseleave", reset);
    };
  }, [ref, intensity, motionAllowed]);
}

interface ScrollFadeOptions {
  offset?: number;
  direction?: "up" | "left" | "right";
}

export function useScrollFade(
  ref: React.RefObject<HTMLElement>,
  { offset = 100, direction = "up" }: ScrollFadeOptions = {}
) {
  const { motionAllowed } = useMotionControl();
  useEffect(() => {
    if (!motionAllowed) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
          el.style.opacity = "1";
          el.style.transform = "translate3d(0,0,0)";
        }
      },
      {
        threshold: 0.1,
        rootMargin: `0px 0px -${offset}px 0px`,
      }
    );

    const initialTransform =
      direction === "left"
        ? "translateX(-40px)"
        : direction === "right"
        ? "translateX(40px)"
        : "translateY(40px)";

    el.style.opacity = "0";
    el.style.transform = initialTransform;
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, offset, direction, motionAllowed]);
}

